import { clamp, hexToRgb, luminance, mixRgb, type Rgb } from '../../engine/math';
import { keyBlendOf } from '../../engine/keyedBackdrop';
import type { ChromeTone } from '../../engine/types';

// Three key lights on the coast: a copper sunset, the violet blue hour as the
// first lamps come on, and a clear moonlit night for the arrival.
const STOPS = {
  skyTop: ['#27315E', '#1C2350', '#0B1230'],
  skyMiddle: ['#7A5F8E', '#3D3F78', '#16224C'],
  skyHorizon: ['#F3A46E', '#C77C8E', '#2D4470'],
  sunGlow: ['#FFC48A', '#F29A8C', '#8FB0E0'],
  cloudLit: ['#F7B78E', '#B07FA4', '#4A5C8A'],
  cloudShade: ['#5D4A72', '#3A355E', '#18213F'],
  islandFar: ['#8A6E8E', '#5A4F7A', '#24345A'],
  islandNear: ['#5C4A6C', '#3A3560', '#18244A'],
  sea: ['#5A5C86', '#323C6A', '#13224A'],
  seaDeep: ['#2A2F55', '#1A2148', '#07112C'],
  seaGlow: ['#FFB27A', '#E59A9C', '#A9C6F0'],
  mist: ['#E8B9A6', '#9C8AB0', '#5A6E9A'],
  rock: ['#4A3B4C', '#2E2A44', '#151A2E'],
  rockLit: ['#B0705A', '#6E5A78', '#3C4C70'],
  rockShade: ['#2A2032', '#1A1830', '#0A0E1E'],
  grass: ['#6E7048', '#3E4C4C', '#1C2A34'],
  grassLit: ['#B4A25A', '#6E6C6C', '#3A4C5C'],
  path: ['#D0A27C', '#8C7A8E', '#4A5470'],
  tower: ['#F2E4D6', '#B9B4CC', '#7F8CA8'],
  towerShade: ['#B39C98', '#6E6A8E', '#3C4666'],
  stripe: ['#C2453E', '#8A3A50', '#5A2A44'],
  foam: ['#FFE6D6', '#D8CCE4', '#A8BCDC'],
} as const;

type ColorName = keyof typeof STOPS;
export type LighthouseColors = Record<ColorName, Rgb>;

export const LIGHTHOUSE_KEYS = 3;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = {} as Record<ColorName, Rgb[]>;
NAMES.forEach((name) => {
  PARSED[name] = STOPS[name].map(hexToRgb);
});
const KEY_COLORS = Array.from({ length: LIGHTHOUSE_KEYS }, (_, key) => {
  const colors = {} as LighthouseColors;
  NAMES.forEach((name) => {
    colors[name] = PARSED[name][key];
  });
  return colors;
});

/** The palette exactly at key light `index` (0 sunset, 1 blue hour, 2 moonlit night). */
export const lighthouseKeyColors = (index: number): LighthouseColors =>
  KEY_COLORS[Math.min(LIGHTHOUSE_KEYS - 1, Math.max(0, Math.round(index) || 0))];

/** The light along the walk: sunset into the blue hour with the questions, night with the arrival. */
export const lighthouseLight = (progress: number, finale: number) =>
  clamp(clamp(progress) * 0.62 + clamp(finale) * 0.38);

export const lighthouseColors = (light: number): LighthouseColors => {
  const { index, amount } = keyBlendOf(light, LIGHTHOUSE_KEYS);
  const colors = {} as LighthouseColors;
  NAMES.forEach((name) => {
    colors[name] = mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  });
  return colors;
};

/** The sky above the coast is dark from sunset on, so the header text stays white. */
export const lighthouseChromeTone = (progress: number): ChromeTone =>
  luminance(lighthouseColors(lighthouseLight(progress, progress >= 0.995 ? 1 : 0)).skyTop) > 0.3 ? 'dark' : 'light';
